import React, { useEffect } from "react";
import { Collapse, Divider, Form, Input, InputNumber, Select, Tag } from "antd";
import DateRangePicker from "../../../../../../../../components/forms/controls/DateRangePicker";
import FilterContainer, { CoreFiltersBaseProps } from "../../../../../../components/FilterContainer";
import { useGetAllBuildingsQuery, useGetAllFloorsQuery, useGetAllLocationsQuery } from "../../../../../../../../store/services/api/building-api";
import { useGetAllAssetTypesQuery, useGetAllSubAssetTypesQuery } from "../../../../../../../../store/services/api/asset-type-api";
import { useGetAllUsersQuery } from "../../../../../../../../store/services/api/user-api";
import { DateTime } from "luxon";
import { WorkOrderStatus ,WorkOrderType} from "../../../../../../../../store/data/entities/work-order";
import { assetPriorityOptions } from "../../../../../../../../store/data/entities/asset";


const { Panel } = Collapse;

type Building = {
  id: string;
  name: string;
};

type Floor = {
  id: string;
  name: string;
  buildingId: string;
};

type Location = {
  id: string;
  name: string;
  floorId: string;
};

export default function SystemAssetsScreenFilters({ filter, onSubmit }: CoreFiltersBaseProps) {
  const { data: allUsers } = useGetAllUsersQuery();
  const { data: buildings } = useGetAllBuildingsQuery(null);
  const { data: floors } = useGetAllFloorsQuery(null);
  const { data: locations } = useGetAllLocationsQuery(null);
  const { data: assetTypes } = useGetAllAssetTypesQuery(null);
  const { data: subAssetTypes } = useGetAllSubAssetTypesQuery(null);

 
  
    // Build maps for names
    const buildingMap: Map<string, string> = new Map(
      (buildings || []).map((b: Building) => [b.id, b.name])
    );
    const floorMap: Map<string, { name: string; buildingId: string }> = new Map(
      (floors || []).map((f: Floor) => [f.id, { name: f.name, buildingId: f.buildingId }])
    );
    // Get current filter values
    const selectedBuildingIds: string[] = filter.getFilter('buildingId') || [];
    const selectedFloorIds: string[] = filter.getFilter('floorId') || [];
    const selectedLocationIds: string[] = filter.getFilter('locationId') || [];

    const selectedAssetTypeIds: string[] = filter.getFilter("assetTypeId") || [];
  const selectedSubAssetTypeIds: string[] = filter.getFilter("subAssetTypeId") || [];
  
    /**
     * Handle Building changes
     */
    const handleOnBuildingChange = (buildingIds: string[]) => {
      filter.setFilter('buildingId', buildingIds);
  
      // prune floors
      const currentFloors: string[] = filter.getFilter('floorId') || [];
      const validFloors = (floors || [])
        .filter(f => buildingIds.includes(f.buildingId))
        .map(f => f.id);
  
      const prunedFloors = currentFloors.filter(floorId =>
        validFloors.includes(floorId)
      );
      filter.setFilter('floorId', prunedFloors);
  
      // prune locations 
      const currentLocations: string[] = filter.getFilter('locationId') || [];
      const validLocations = (locations || [])
        .filter(l => validFloors.includes(l.floorId))
        .map(l => l.id);
  
      const prunedLocations = currentLocations.filter(locId =>
        validLocations.includes(locId)
      );
      filter.setFilter('locationId', prunedLocations);
    };
  
    /**
     * Handle Floor changes
     */
    const handleOnFloorChange = (floorIds: string[]) => {
      filter.setFilter('floorId', floorIds);
  
      // prune locations
      const currentLocations: string[] = filter.getFilter('locationId') || [];
      const validLocations = (locations || [])
        .filter(l => floorIds.includes(l.floorId))
        .map(l => l.id);
  
      const prunedLocations = currentLocations.filter(locId =>
        validLocations.includes(locId)
      );
      filter.setFilter('locationId', prunedLocations);
    };
  
    /**
     * Floors filtered by buildings
     */
    const filteredFloors: Floor[] = !selectedBuildingIds.length
      ? (floors || [])
      : (floors || []).filter(f => selectedBuildingIds.includes(f.buildingId));
  
    const sortedFloors = filteredFloors
      .map(f => {
        const buildingName = buildingMap.get(f.buildingId) ?? '';
        return { ...f, label: `[${buildingName}] ${f.name}` };
      })
      .sort((a, b) => {
        const buildingA = buildingMap.get(a.buildingId) ?? '';
        const buildingB = buildingMap.get(b.buildingId) ?? '';
        return buildingA === buildingB
          ? a.name.localeCompare(b.name)
          : buildingA.localeCompare(buildingB);
      });
  
    /**
     * Locations filtered by floors
     */
    const filteredLocations: Location[] = !selectedFloorIds.length
      ? (locations || [])
      : (locations || []).filter(l => selectedFloorIds.includes(l.floorId));
  
    const sortedLocations = filteredLocations
      .map(l => {
        const floor = floorMap.get(l.floorId);
        const buildingName = floor ? buildingMap.get(floor.buildingId) ?? '' : '';
        const floorName = floor?.name ?? '';
        return {
          ...l,
          label: `[${buildingName}] [${floorName}] ${l.name}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  
    // Create a map for location labels
    const locationLabelMap: Map<string, string> = new Map(
      sortedLocations.map(l => [l.id, l.label])
    );
    // Create a map for floor labels
    const floorLabelMap: Map<string, string> = new Map(
      sortedFloors.map(l => [l.id, l.label])
    );

    const handleOnAssetTypeChange = (assetTypeIds: string[]) => {
    filter.setFilter("assetTypeId", assetTypeIds);

    // prune sub assets
    const currentSubAssets: string[] = filter.getFilter("subAssetTypeId") || [];
    const validSubAssets = (subAssetTypes || [])
      .filter((sa) => assetTypeIds.includes(sa.assetTypeId)) // assumes subAsset has `assetTypeId` reference
      .map((sa) => sa.id);

    const prunedSubAssets = currentSubAssets.filter((saId) =>
      validSubAssets.includes(saId)
    );
    filter.setFilter("subAssetTypeId", prunedSubAssets);
  };
  

    const filteredSubAssets = !selectedAssetTypeIds.length
    ? subAssetTypes || []
    : (subAssetTypes || []).filter((sa) =>
        selectedAssetTypeIds.includes(sa.assetTypeId)
      );

  // Create label with AssetType
  const subAssetLabelMap: Map<string, string> = new Map(
    filteredSubAssets.map((sa) => {
      const assetName =
        assetTypes?.find((a) => a.id === sa.assetTypeId)?.name ?? "";
      return [sa.id, `[${assetName}] ${sa.name}`];
    })
  );

  const sortedSubAssets = filteredSubAssets
    .map((sa) => ({
      ...sa,
      label: subAssetLabelMap.get(sa.id) || sa.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
    
    // Custom tag render for locations with wrapped text
    const locationTagRender = (props: any) => {
      const { label, value, closable, onClose } = props;
      const locationLabel = locationLabelMap.get(value) || label;
      
      return (
        <Tag
          closable={closable}
          onClose={onClose}
          style={{ 
            margin: '2px', 
            padding: '2px 6px',
            maxWidth: '100%',
            whiteSpace: 'normal',
            lineHeight: '1.4em',
            height: 'auto'
          }}
        >
          {locationLabel}
        </Tag>
      );
    };

    const subAssetTagRender = (props: any) => {
      const { label, value, closable, onClose } = props;
      const subAssetLabel = subAssetLabelMap.get(value) || label;
      
      return (
        <Tag
          closable={closable}
          onClose={onClose}
          style={{ 
            margin: '2px', 
            padding: '2px 6px',
            maxWidth: '100%',
            whiteSpace: 'normal',
            lineHeight: '1.4em',
            height: 'auto'
          }}
        >
          {subAssetLabel}
        </Tag>
      );
    };

    // Custom tag render for locations with wrapped text
    const floorTagRender = (props: any) => {
      const { label, value, closable, onClose } = props;
      const floorLabel = floorLabelMap.get(value) || label;
      
      return (
        <Tag
          closable={closable}
          onClose={onClose}
          style={{ 
            margin: '2px', 
            padding: '2px 6px',
            maxWidth: '100%',
            whiteSpace: 'normal',
            lineHeight: '1.4em',
            height: 'auto'
          }}
        >
          {floorLabel}
        </Tag>
      );
    };

  // Work Orders operator
  useEffect(() => {
    if (!filter.getFilter('noWorkOrdersOperator')) {
      filter.setFilter('noWorkOrdersOperator', '=');
    }
  }, [filter]);
  const operator = filter.getFilter('noWorkOrdersOperator') || '=';
  const { Option } = Select;
  const selectAfter = (
    <Select
      value={operator}
      style={{ minWidth: 140 }}
      onChange={val => filter.setFilter('noWorkOrdersOperator', val ?? '=')}
    >
      <Option value=">">Greater Than</Option>
      <Option value="<">Less Than</Option>
      <Option value="=">Equal To</Option>
    </Select>
  );



  return (
    <FilterContainer onResetFilters={filter.resetFilters} onSubmit={onSubmit} isSubmitting={undefined}>
      <Collapse defaultActiveKey={['workOrders', 'location', 'assets', 'createdInfo','assetInfo']} ghost>
        {/* Work Orders Section */}
        <Panel header="Work Orders" key="workOrders">
          <Form layout="vertical">
            <Form.Item label="Number of Work Orders">
              <InputNumber
                addonAfter={selectAfter}
                value={filter.getFilter('noWorkOrders')}
                onChange={val => filter.setFilter('noWorkOrders', val ?? undefined)}
              />
            </Form.Item>
            <Form.Item label="Work Order Status">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).children  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('statuses')}
                onChange={val => filter.setFilter('statuses', val)}
              >
                <Select.Option value={WorkOrderStatus.PendingApproval}>Pending Approval</Select.Option>
                <Select.Option value={WorkOrderStatus.Approved}>Approved</Select.Option>
                <Select.Option value={WorkOrderStatus.Rejected}>Rejected</Select.Option>
                <Select.Option value={WorkOrderStatus.InProgress}>In Progress</Select.Option>
                <Select.Option value={WorkOrderStatus.Completed}>Completed</Select.Option>
                <Select.Option value={WorkOrderStatus.Closed}>Closed</Select.Option>
                <Select.Option value={WorkOrderStatus.Cancelled}>Cancelled</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label='Work Order Type'>
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).children  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('type')}
                onChange={val => filter.setFilter('type', val)}
              >
                <Select.Option value={WorkOrderType.Preventative}>Preventative</Select.Option>
                <Select.Option value={WorkOrderType.Corrective}>Corrective</Select.Option>
                <Select.Option value={WorkOrderType.Improvement}>Improvement</Select.Option>
              </Select>
            </Form.Item>
            
            <Form.Item label='Work Order Reference'>
              <Input
                allowClear
                value={filter.getFilter('WOReference')}
                onChange={e => filter.setFilter('WOReference', e.target.value)}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Location Section */}
        <Panel header="Location" key="location">
          <Form layout='vertical'>
            <Form.Item label="Building">
            <Select
              mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const label = String(option?.label ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              value={selectedBuildingIds}
              onChange={handleOnBuildingChange}
              options={buildings?.map(item => ({
                label: item.name,
                value: item.id,
              }))}
            />
          </Form.Item>
  
          <Form.Item label="Floor">
            <Select
              mode="multiple"
              allowClear
              value={selectedFloorIds}
              onChange={handleOnFloorChange}
              tagRender={floorTagRender}
              showSearch
              filterOption={(input, option) => {
                const text = String((option as any).searchText || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              dropdownRender={menu => (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {menu}
                </div>
              )}
              
              optionLabelProp="label"
              options={sortedFloors.map(item => ({
                label: (
                  <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                    {item.label}
                  </div>
                ),
                value: item.id,
                searchText: item.label,
              }))}
            />
          </Form.Item>
  
          <Form.Item label="Location">
            <Select
              mode="multiple"
              allowClear
              value={selectedLocationIds}
              onChange={(val) => filter.setFilter('locationId', val)}
              tagRender={locationTagRender}
              filterOption={(input, option) => {
                const text = String((option as any).searchText || '');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              dropdownRender={menu => (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {menu}
                </div>
              )}
              optionLabelProp="label"
              options={sortedLocations.map(item => ({
                label: (
                  <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                    {item.label}
                  </div>
                ),
                value: item.id,
                searchText: item.label,
              }))}
            />
          </Form.Item>
  
          </Form>
        </Panel>

        {/* Assets Section */}
        <Panel header="Assets" key="assets">
          <Form layout="vertical">
            <Form.Item label="Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedAssetTypeIds}
                onChange={handleOnAssetTypeChange}
                options={assetTypes?.map((item) => ({ label: item.name, value: item.id }))}
              />
            </Form.Item>



            <Form.Item label="Sub Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).searchText || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedSubAssetTypeIds}
                onChange={(val) => filter.setFilter('subAssetTypeId', val)}
                tagRender={subAssetTagRender}
                dropdownRender={menu => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {menu}
                  </div>
                )}
                optionLabelProp="label"
                options={sortedSubAssets.map(item => ({
                  label: (
                    <div style={{ whiteSpace: 'normal', lineHeight: '1.4em' }}>
                      {item.label}
                    </div>
                  ),
                  value: item.id,
                  searchText: item.label,
                }))}
              />
            </Form.Item>
          </Form>
        </Panel>
        {/* Created Info Section */}
        <Panel header="Created Info" key="createdInfo">
          <Form layout="vertical">
            <Form.Item label="Created By">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const text = String((option as any).label  || '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter('createdByUserId')}
                onChange={val => filter.setFilter('createdByUserId', val)}
                options={allUsers?.map(u => ({ label: u.name, value: u.id }))}
              />
            </Form.Item>
            <Form.Item label="Created On">
              <DateRangePicker
                onStartDateChange={val => filter.setFilter('createdOnStartDate', val?.toISO())}
                onEndDateChange={val => filter.setFilter('createdOnEndDate', val?.toISO())}
                startDateValue={filter.getFilter('createdOnStartDate') ? DateTime.fromISO(filter.getFilter('createdOnStartDate')) : undefined}
                endDateValue={filter.getFilter('createdOnEndDate') ? DateTime.fromISO(filter.getFilter('createdOnEndDate')) : undefined}
              />
            </Form.Item>
          </Form>
        </Panel>

        {/* Created Info Section */}
        <Panel header="Asset Info" key="assetInfo">
          <Form layout="vertical">
            <Form.Item label='Ticket Status'>
          <Select
            defaultValue=''
            value={filter.getFilter('ticketStatus')}
            onChange={val => filter.setFilter('ticketStatus', val)}
          >
            <Select.Option value={-1}>All</Select.Option>
            <Select.Option value={1}>Outstanding</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label='Asset Ranking'>
          <Select
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) => {
              const text = String((option as any).label  || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            options={assetPriorityOptions} 
            value={filter.getFilter('assetPriority')}
            onChange={val => filter.setFilter('assetPriority', val)}
          >
          </Select>
        </Form.Item>
        <Form.Item label='Code'>
          <Input
            allowClear
            value={filter.getFilter('code')}
            onChange={e => filter.setFilter('code', e.target.value)}
          />
        </Form.Item>
        <Form.Item label='Description'>
          <Input
            allowClear
            value={filter.getFilter('description')}
            onChange={e => filter.setFilter('description', e.target.value)}
          />
        </Form.Item>
        <Form.Item label='CoreRFID QR'>
          <Input
            allowClear
            value={filter.getFilter('uniqueReference')}
            onChange={e => filter.setFilter('uniqueReference', e.target.value)}
          />
        </Form.Item>
          </Form>
        </Panel>
      </Collapse>
    </FilterContainer>
  );
}
